import { withHandlers } from 'recompose'

import { CARDS } from '@booking/reducers'
import {
    ratesPendingSelector,
    selectedVehicleSelector,
    lastBookingBasketItemSelector,
    isSameTicketInBasketSelector,
    selectedCreditCardSelector,
    disabledCardsSelector,
    selectedSeasonTicketSelector,
    waitingListKeySelector,
    waitingListPendingSelector,
    parkingModeSelector,
} from '@booking/selectors'
import { setLastBookingDetails, clearSelectedRate, setRawPromoCode } from '@booking/actions'

import {
    makePurchasePendingSelector,
    creditCardsSelector,
    numberOfItemsSelector,
    addressToStringSelector,
} from 'basket/selectors'
import { makePurchase, addToBasket } from 'basket/actions'
import { userSelector } from 'auth/selectors'
import { withRouter } from 'i18n/utils'
import { CardSection } from '@components/CardSection'
import { validationErrorsInitialState, getBookingValidationErrors } from '../utilities'

import WaitingList from '../WaitingList'

import { toggleAuthVisible } from 'header/actions'
import { ACTIVE_FORM_LOGIN, ACTIVE_FORM_SIGNUP } from 'header/constants'
import ErrorMessage from 'shared/components/ErrorMessage'
import Button from '@components/Button'
import { SEARCH_MODE_MONTHLY, SEARCH_MODE_EVENTS, SEARCH_MODE_PREPAID } from 'search/constants'
import VehicleSection from '@booking/components/ConfirmSection/VehicleSection'
import PaySection from '@booking/components/ConfirmSection/PaySection'
import MonthlyAddressSection from '@booking/components/ConfirmSection/MonthlyAddressSection'

const Confirmation = ({
    grsId,
    clearSelectedRate,
    selectedVehicles,
    rate,
    setLastBookingDetails,
    user,
    ratesPending,
    selectedCreditCard,
    onClickLogin,
    onClickSignUp,
    makePurchasePending,
    addToBasket,
    makePurchase,
    lastBookingBasketItem,
    numberOfItemsInBasket,
    history,
    isSameTicketInBasket,
    disabledCards,
    selectedSeasonTicket,
    setRawPromoCode,
    waitingListKey,
    waitingListPending,
    deliveryAddress,
    parkingMode,
}) => {
    const isMonthly = parkingMode === SEARCH_MODE_MONTHLY
    const isPrepaid = parkingMode === SEARCH_MODE_PREPAID
    const isEventFlow = parkingMode === SEARCH_MODE_EVENTS
    const isAddAnotherBookingFlow = numberOfItemsInBasket > 0
    const [currentItemIndex, setCurrentItemIndex] = React.useState(numberOfItemsInBasket + 1)
    const [validationErrors, setValidationErrors] = React.useState(validationErrorsInitialState)
    const isOnWaitingList =
        selectedSeasonTicket.ticket &&
        selectedSeasonTicket.ticket.isOnWaitingList &&
        waitingListPending !== true &&
        !waitingListKey
    const [waitingListModalOpen, setWaitingListModalOpen] = React.useState(false)

    const isLastBookingBasketItemEmpty = lastBookingBasketItem && Object.keys(lastBookingBasketItem.rate).length === 1

    const areRatesPending = ratesPending && !(ratesPending instanceof Error)

    const purchaseError = makePurchasePending instanceof Error ? makePurchasePending : ''

    const onPayForParkingClick = () => {
        if (!validateBooking({ purchaseValidation: true })) return
        if (rate.isOnWaitingList && !waitingListKey) return

        window.grecaptcha.ready(() => {
            window.grecaptcha.execute(GOOGLE_RECAPTCHA_KEY, { action: 'submit' }).then(token => {
                makePurchase(lastBookingBasketItem, token)
            })
        })
    }

    const onGoToBasketClick = () => {
        if (!validateBooking({ purchaseValidation: true })) return

        addToBasket(lastBookingBasketItem)
        clearSelectedRate()
        history.push('basket')
    }

    const onAddAnotherBooking = () => {
        if (!validateBooking()) return

        addToBasket(lastBookingBasketItem)
        clearSelectedRate()
        setCurrentItemIndex(currentItemIndex + 1)
    }

    const displayError = () => {
        for (const elem of Object.keys(validationErrorsInitialState)) {
            if (validationErrors[elem]) {
                return <ErrorMessage messages={[validationErrors[elem]]} />
            }
        }
    }

    const validateBooking = ({ purchaseValidation = false } = {}) => {
        const { errors, isValid } = getBookingValidationErrors({
            rate,
            parkingMode,
            deliveryAddress,
            selectedVehicles,
            isSameTicketInBasket,
            purchaseValidation,
            selectedCreditCard,
        })

        setValidationErrors(errors)
        return isValid
    }

    const updateValidation = objectToUpdate =>
        setValidationErrors({
            ...validationErrors,
            ...objectToUpdate,
        })

    React.useEffect(() => {
        setValidationErrors({
            ...validationErrors,
            isSameTicketInBasketError: false,
        })
    }, [isSameTicketInBasket])

    React.useEffect(() => {
        setRawPromoCode('')
        setValidationErrors(validationErrorsInitialState)
    }, [numberOfItemsInBasket])

    React.useEffect(() => {
        setLastBookingDetails({
            id: currentItemIndex,
            grsId,
            rate,
            waitingListKey,
        })
    }, [grsId, rate, waitingListKey])

    return (
        <>
            {user ? (
                isOnWaitingList ? (
                    <CardSection>
                        <Button size="large" className="ml-auto" onClick={() => setWaitingListModalOpen(true)}>
                            Add me to list
                        </Button>
                        {waitingListModalOpen && (
                            <WaitingList grsId={grsId} onClose={() => setWaitingListModalOpen(false)} />
                        )}
                    </CardSection>
                ) : (
                    <>
                        {isMonthly && <MonthlyAddressSection disabled={!!disabledCards[CARDS.CONFIRM_VEHICLE]} />}
                        {isAddAnotherBookingFlow && displayError()}
                        {!isPrepaid && (
                            <VehicleSection
                                rate={rate}
                                onAddAnotherBooking={onAddAnotherBooking}
                                areRatesPending={areRatesPending}
                                disabled={disabledCards[CARDS.CONFIRM_VEHICLE] || isLastBookingBasketItemEmpty}
                                isEventFlow={isEventFlow}
                                updateValidation={updateValidation}
                                currentItemIndex={currentItemIndex}
                            />
                        )}
                        {purchaseError && <ErrorMessage messages={[purchaseError.message]} />}
                        {!isAddAnotherBookingFlow && displayError()}
                        <PaySection
                            disabled={
                                !!(disabledCards[CARDS.CONFIRM_VEHICLE] | disabledCards[CARDS.PAY_FOR_PARKING]) ||
                                isLastBookingBasketItemEmpty
                            }
                            purchaseError={purchaseError}
                            onPayForParkingClick={onPayForParkingClick}
                            onGoToBasketClick={onGoToBasketClick}
                            isAddAnotherBookingFlow={isAddAnotherBookingFlow}
                            updateValidation={updateValidation}
                        />
                    </>
                )
            ) : (
                <CardSection className="disabled confirm" title={<T>booking.confirm.and.pay</T>}>
                    <p>{<T>booking.confirm.and.pay.info</T>}</p>
                    <div className="row">
                        <div className="col-xs-12 col-sm-3">
                            <Button size="large" block onClick={onClickLogin}>
                                <T>booking.btn.login</T>
                            </Button>
                        </div>
                        <div className="col-xs-12 col-sm-3">
                            <Button size="large" block onClick={onClickSignUp} btnStyle="outline">
                                <T>booking.btn.signup</T>
                            </Button>
                        </div>
                    </div>
                </CardSection>
            )}
        </>
    )
}

export default compose(
    withRouter,
    connect(
        createStructuredSelector({
            numberOfItemsInBasket: numberOfItemsSelector,
            makePurchasePending: makePurchasePendingSelector,
            cards: creditCardsSelector,
            ratesPending: ratesPendingSelector,
            user: userSelector,
            selectedVehicles: selectedVehicleSelector,
            selectedCreditCard: selectedCreditCardSelector,
            lastBookingBasketItem: lastBookingBasketItemSelector,
            isSameTicketInBasket: isSameTicketInBasketSelector,
            disabledCards: disabledCardsSelector,
            selectedSeasonTicket: selectedSeasonTicketSelector,
            waitingListKey: waitingListKeySelector,
            waitingListPending: waitingListPendingSelector,
            deliveryAddress: addressToStringSelector,
            parkingMode: parkingModeSelector,
        }),
        {
            makePurchase,
            addToBasket,
            clearSelectedRate,
            toggleAuthVisible,
            setLastBookingDetails,
            setRawPromoCode,
        }
    ),
    withHandlers({
        onClickLogin: ({ toggleAuthVisible }) => () => {
            toggleAuthVisible(ACTIVE_FORM_LOGIN)
        },
        onClickSignUp: ({ toggleAuthVisible }) => () => {
            toggleAuthVisible(ACTIVE_FORM_SIGNUP)
        },
    })
)(Confirmation)
